import ReactMarkdown from 'react-markdown';
import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDashboard } from './Dashboardcontext';
import { 
  Bot,
  Share,
  User,
  Menu,
  ChevronDown,
  Edit3,
  Paperclip,
  Plus,
  Sparkles,
  ThumbsUp,
  Repeat2,
  MessageCircle,
  MoreHorizontal,
  Calendar,
  Download,
  Eye,
  ArrowLeft,
  ExternalLink,
  Trash2,
  X,
  Send,
  ArrowUp,
  Copy,
  Check,
  Edit2,
  Bookmark
} from 'lucide-react';
import Sidebar from './Sidebar';
import Header from './Header';
import LinkedInConnectionModal from './LinkedInConnectionModal';
import { useLinkedInAccount } from '../hooks/useLinkedInAccount';

const CreatePostPage = () => {
  const [activeTab, setActiveTab] = useState('create-post');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // PHASE 2: Semantic string panel driver replaces numerical step counter
  const [panelMode, setPanelMode] = useState('chat'); 

  const [postDescription, setPostDescription] = useState('');
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [scheduleYear, setScheduleYear] = useState(new Date().getFullYear());
  const [scheduleMonth, setScheduleMonth] = useState(new Date().getMonth());
  const [scheduleDay, setScheduleDay] = useState(new Date().getDate());
  const [scheduleHour, setScheduleHour] = useState(12);
  const [scheduleMinute, setScheduleMinute] = useState(0);
  const [scheduleAMPM, setScheduleAMPM] = useState('AM');
  const [scheduleTimezone, setScheduleTimezone] = useState('UTC+05:30 (IST)');
  const [postObjective, setPostObjective] = useState('');
  const [toneStyle, setToneStyle] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isGeneratingDrafts, setIsGeneratingDrafts] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [isLoadingPost, setIsLoadingPost] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const { fetchDashboardData } = useDashboard();
  const [previewDraft, setPreviewDraft] = useState(null);
  const [showLinkedInModal, setShowLinkedInModal] = useState(false);
  const [linkedinStatus, setLinkedinStatus] = useState('guest');
  const [imageBlobUrls, setImageBlobUrls] = useState({});
  const { accountId, accountType } = useLinkedInAccount();

  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingMessageContent, setEditingMessageContent] = useState('');
  const [editInputValue, setEditInputValue] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [copiedDraftId, setCopiedDraftId] = useState(null);

  const [draftSeed, setDraftSeed] = useState(null); 
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [showSavedDrafts, setShowSavedDrafts] = useState(false);
  const [savedDraftToast, setSavedDraftToast] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const [localContent, setLocalContent] = useState('');
  const [localImageUrl, setLocalImageUrl] = useState(null);
  const [extraInput, setExtraInput] = useState('');
  const [isUploadingLocal, setIsUploadingLocal] = useState(false);

  // --- Group posting state ---
  const [postDestination, setPostDestination] = useState('single'); // 'single' | 'groups'
  const [linkedinGroups, setLinkedinGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isScrapingGroups, setIsScrapingGroups] = useState(false);
  const [showAddGroupForm, setShowAddGroupForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupUrl, setNewGroupUrl] = useState('');
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [groupWindowMinutes, setGroupWindowMinutes] = useState(180);
  const [isPostingToGroups, setIsPostingToGroups] = useState(false);
  const [groupBatch, setGroupBatch] = useState(null); // { batch_id, total_groups, window_minutes, jobs }
  const [groupBatchStatus, setGroupBatchStatus] = useState(null); // polled { batch, jobs }

  const loadImageWithAuth = async (imageUrl) => {
  if (!imageUrl || imageBlobUrls[imageUrl]) return;
  setImageBlobUrls(prev => ({ ...prev, [imageUrl]: imageUrl }));
};

  const uploadImageToSupabase = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (ev) => {
      try {
        const base64 = ev.target.result.split(',')[1];
        const response = await fetch('https://n8n.acespireconsulting.com/webhook/dev/upload-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: localStorage.getItem('userId'),
            fileName: file.name,
            base64: base64,
            mimeType: file.type
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          const finalUrl = result.image_url || result.media_url || result.publicUrl || result.url;
          if (finalUrl) {
            resolve(finalUrl);
          } else {
            reject(new Error('Upload succeeded but no valid image URL was returned in payload structure.'));
          }
        } else {
          reject(new Error('Upload failed according to backend response status'));
        }
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsDataURL(file);
  });
};

  useEffect(() => {
    drafts.forEach(draft => {
      if (draft.image) {
        loadImageWithAuth(draft.image);
      }
    });
  }, [drafts]);

  useEffect(() => {
    return () => {
      Object.values(imageBlobUrls).forEach(blobUrl => {
        if (blobUrl.startsWith('blob:')) {
          URL.revokeObjectURL(blobUrl);
        }
      });
    };
  }, []);

  const saveSchedulingState = () => {
    const schedulingState = {
      panelMode,
      selectedDraft,
      drafts,
      postDescription,
      postObjective,
      toneStyle,
      scheduleYear,
      scheduleMonth,
      scheduleDay,
      scheduleHour,
      scheduleMinute,
      scheduleAMPM,
      scheduleTimezone,
      timestamp: Date.now()
    };
    
    localStorage.setItem('schedulingState', JSON.stringify(schedulingState));
  };

  const clearSchedulingState = () => {
    localStorage.removeItem('schedulingState');
  };

  const location = useLocation();
  const navigate = useNavigate();

  const handleCopyMessage = async (messageId, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = content;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    }
  };

  const handlePostToLinkedIn = (message) => {
    setDraftSeed({
      content: message.content || '',
      imageUrl: message.image_url || null,
      sourceMessageId: message.id
    });
    
    setLocalContent(message.content || '');
    setLocalImageUrl(message.image_url || null);
    setExtraInput('');
    
    setPanelMode('draft-seed');
    window.history.pushState({ page: 'draft-seed' }, '');
  };

  const handleRegenerateMessage = async (messageId) => {
    const messageIndex = chatMessages.findIndex(m => m.id === messageId);
    if (messageIndex === -1) return;
    
    const targetMessage = chatMessages[messageIndex];
    let currentInput = "";
    let trimmedHistory = [];

    if (targetMessage.type === 'user') {
      currentInput = targetMessage.content;
      trimmedHistory = chatMessages.slice(0, messageIndex);
    } else {
      const prevUserMsg = [...chatMessages]
        .slice(0, messageIndex)
        .reverse()
        .find(m => m.type === 'user');
        
      if (!prevUserMsg) return;
      currentInput = prevUserMsg.content;
      trimmedHistory = chatMessages.slice(0, chatMessages.findIndex(m => m.id === prevUserMsg.id));
    }
    const contextUserMsg = chatMessages.find(m => m.content === currentInput && m.type === 'user');
    setChatMessages([...trimmedHistory, contextUserMsg || { id: Date.now(), type: 'user', content: currentInput, attachedFiles: [] }]);
    
    setIsAIResponding(true);
    
    try {
      const payload = {
        sessionId,
        message: currentInput,
        postObjective,
        toneStyle,
        attachedFiles: targetMessage.type === 'user' ? (targetMessage.attachedFiles || []) : (chatMessages.find(m => m.content === currentInput)?.attachedFiles || []),
        chatHistory: trimmedHistory.slice(-10),
        isRegenerate: true
      };
      
      const response = await fetch('https://n8n.acespireconsulting.com/webhook/dev/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const result = await response.json();
        const data = Array.isArray(result) ? result[0] : result;
        setChatMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'ai',
          content: data.output || data.message || '',
          image_url: data.permanentChatImageUrl || data.image_url || null,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }]);
      }
    } catch (err) {
      console.error('Regenerate failed:', err);
    } finally {
      setIsAIResponding(false);
    }
  };

  const handleCopyDraft = async (draftId, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedDraftId(draftId);
      setTimeout(() => setCopiedDraftId(null), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = content;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedDraftId(draftId);
      setTimeout(() => setCopiedDraftId(null), 2000);
    }
  };

  const handleSaveDraft = async (draft) => {
    try {
      const response = await fetch('https://n8n.acespireconsulting.com/webhook/dev/save-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: localStorage.getItem('userId'),
          linkedinAccountId: localStorage.getItem('selectedLinkedInAccountId'),
          content: draft.content,
          mediaUrl: draft.image || null,
          prompt: postDescription,
          postObjective,
          toneStyle
        })
      });
      
      const result = await response.json();
      if (result.success) {
        setSavedDraftToast('Draft saved!');
        setTimeout(() => setSavedDraftToast(''), 2000);
      }
    } catch (err) {
      console.error('Save draft failed:', err);
    }
  };

  const fetchSavedDrafts = async () => {
    try {
      const response = await fetch('https://n8n.acespireconsulting.com/webhook/dev/get-saved-drafts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: localStorage.getItem('userId') })
      });
      const result = await response.json();
      if (result.success) setSavedDrafts(result.drafts || []);
    } catch (err) {
      console.error('Fetch saved drafts failed:', err);
    }
  };

  const handleDeleteSavedDraft = async (draftId) => {
    try {
      await fetch('https://n8n.acespireconsulting.com/webhook/dev/delete-saved-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ draftId, userId: localStorage.getItem('userId') })
      });
      setSavedDrafts(prev => prev.filter(d => d.id !== draftId));
    } catch (err) {
      console.error('Delete saved draft failed:', err);
    }
  };

  const handleSelectSavedDraft = (draft) => {
    setDrafts([{
      id: 1,
      user: 'Acespire Solutions',
      date: 'Saved draft',
      content: draft.content,
      image: draft.media_url,
      media_url: draft.media_url,
      facts: `Saved on ${new Date(draft.created_at).toLocaleDateString()}`,
      engagement: { likes: 0, comments: 0, reposts: 0 }
    }]);
    setSelectedDraft(1);
    setShowSavedDrafts(false);
    setPanelMode('schedule');
    window.history.pushState({ page: 'schedule' }, '');
  };

  useEffect(() => {
    const savedState = localStorage.getItem('schedulingState');
    
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        
        const isRecent = Date.now() - state.timestamp < 5 * 60 * 1000;
        
        if (isRecent) {
          if (state.panelMode) {
            setPanelMode(state.panelMode);
          } else if (state.currentStep) {
            const legacyMap = { 1: 'chat', 2: 'drafts', 3: 'schedule', 4: 'confirmation' };
            setPanelMode(legacyMap[state.currentStep] || 'chat');
          }
          
          setSelectedDraft(state.selectedDraft);
          setDrafts(state.drafts || []);
          setPostDescription(state.postDescription || '');
          setPostObjective(state.postObjective || '');
          setToneStyle(state.toneStyle || '');
          setScheduleYear(state.scheduleYear);
          setScheduleMonth(state.scheduleMonth);
          setScheduleDay(state.scheduleDay);
          setScheduleHour(state.scheduleHour);
          setScheduleMinute(state.scheduleMinute);
          setScheduleAMPM(state.scheduleAMPM);
          setScheduleTimezone(state.scheduleTimezone);
          
          clearSchedulingState();
        } else {
          clearSchedulingState();
        }
      } catch (error) {
        clearSchedulingState();
      }
    }
  }, []);

  const handleSaveEdit = async (messageId, newContent) => {
    const messageIndex = chatMessages.findIndex(m => m.id === messageId);
    const originalMessage = chatMessages[messageIndex]; 

    const updatedMessage = {
      ...originalMessage,
      content: newContent,
      edited: true,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    const trimmedHistory = [
      ...chatMessages.slice(0, messageIndex),
      updatedMessage,
    ];

    setChatMessages(trimmedHistory);
    setEditingMessageId(null);
    setIsAIResponding(true);

    try {
      const payload = {
        sessionId,
        message: newContent,
        postObjective,
        toneStyle,
        attachedFiles: originalMessage.attachedFiles || [],
        chatHistory: trimmedHistory.slice(-10),
        isEdit: true,
      };

      const response = await fetch('https://n8n.acespireconsulting.com/webhook/dev/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        // signal: AbortSignal.timeout(30000),
      });

      if (response.ok) {
        const result = await response.json();
        const data = Array.isArray(result) ? result[0] : result;
        setChatMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'ai',
          content: data.output || data.message || 'I understand. How can I help you refine this further?',
          image_url: data.permanentChatImageUrl || data.image_url || null,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        }]);
      } else {
        setChatMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'ai',
          content: `Got your updated message: "${newContent}". Would you like me to refine this further or generate drafts?`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        }]);
      }
    } catch {
      setChatMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'ai',
        content: `Understood — "${newContent}". Let me know how you'd like to proceed!`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      }]);
    } finally {
      setIsAIResponding(false);
    }
  };

  useEffect(() => {
    window.history.pushState({ page: panelMode }, '');
    
    const handlePopState = (event) => {
      event.preventDefault();
      
      const backMap = {
        'confirmation': 'schedule',
        'schedule': 'drafts',
        'drafts': 'draft-seed',
        'draft-seed': 'chat',
        'chat': null
      };
      
      const prevPanel = backMap[panelMode];
      if (prevPanel) {
        setPanelMode(prevPanel);
        window.history.pushState({ page: prevPanel }, '');
      } else {
        navigate('/dashboard');
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [panelMode, navigate]);

  useEffect(() => {
    const accountId = localStorage.getItem('selectedLinkedInAccountId');
    const status = localStorage.getItem('linkedinStatus');
    
    if (accountId && status === 'connected') {
      setLinkedinStatus('connected');
    } else {
      setLinkedinStatus('guest');
    }
  }, []);

  const [editMode, setEditMode] = useState(null);
  const [postIdToEdit, setPostIdToEdit] = useState(null);
  
  const [chatMessages, setChatMessages] = useState([]);
  const [isAIResponding, setIsAIResponding] = useState(false);
  
  const textareaRef = useRef(null);
  const chatContainerRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  useEffect(() => {
    const currentCount = chatMessages.length;
    const prevCount = prevMessageCountRef.current;
    
    if (currentCount > prevCount && chatContainerRef.current) {
      requestAnimationFrame(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTo({
            top: chatContainerRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      });
    }
    
    prevMessageCountRef.current = currentCount;
  }, [chatMessages]);

  useEffect(() => {
    const handleResumeChatSession = (e) => {
      if (e.detail) handleSelectRecentChat(e.detail);
    };

    window.addEventListener('resumeChatSession', handleResumeChatSession);
    return () => window.removeEventListener('resumeChatSession', handleResumeChatSession);
  }, []);
  
  useEffect(() => {
    const generateSessionId = () => {
      let existingSession = localStorage.getItem('chatSessionId');
      
      if (!existingSession) {
        const timestamp = Date.now();
        const randomStr = Math.random().toString(36).substring(2, 15);
        existingSession = `session_${timestamp}_${randomStr}`;
        localStorage.setItem('chatSessionId', existingSession);
      }
      
      setSessionId(existingSession);
    };

    generateSessionId();
  }, []);

  const handleDownloadImage = async (imageUrl, draftId) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `draft-${draftId}-image.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download image:', error);
    alert('Failed to download image. Please try again.');
  }
};

  const createNewSession = () => {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 15);
    const newSessionId = `session_${timestamp}_${randomStr}`;
    localStorage.setItem('chatSessionId', newSessionId);
    setSessionId(newSessionId);
    setChatMessages([]);
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  useEffect(() => {
    if (textareaRef.current && !isGeneratingDrafts && !isAIResponding) {
      textareaRef.current.focus();
    }
  }, [postDescription, isGeneratingDrafts, isAIResponding]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const editId = searchParams.get('edit');
    const rescheduleId = searchParams.get('reschedule');
    
    if (editId) {
      setIsLoadingPost(true);
      setEditMode('edit');
      setPostIdToEdit(editId);
      fetchPostData(editId, 'edit').finally(() => setIsLoadingPost(false));
    } else if (rescheduleId) {
      setIsLoadingPost(true);
      setEditMode('reschedule');
      setPostIdToEdit(rescheduleId);
      fetchPostData(rescheduleId, 'reschedule').finally(() => setIsLoadingPost(false));
    }
  }, [location.search]);

  const handleTextInput = (e) => {
  setPostDescription(e.target.value);
};

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (postDescription.trim()) {
        sendMessageToAI();
      }
    }
  };

  const handleSelectRecentChat = (session) => {
    localStorage.setItem('chatSessionId', session.session_id);
    setSessionId(session.session_id);
    setPanelMode('chat'); 
    setIsAIResponding(true);
    
    fetch('https://n8n.acespireconsulting.com/webhook/dev/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        sessionId: session.session_id,
        userId: localStorage.getItem('userId'),
        message: "Fetch History Sequence",
        chatHistory: [],
        isFetchHistory: true 
      })
    })
    .then(res => res.json())
    .then(data => {
      const payload = Array.isArray(data) ? data[0] : data;
      if (payload && payload.history) {
        setChatMessages(payload.history);
      } else {
        setChatMessages([]);
      }
    })
    .catch((err) => {
      console.error("Failed to load history", err);
      setChatMessages([]);
    })
    .finally(() => setIsAIResponding(false));
  };

  const sendMessageToAI = async () => {
    if (!postDescription.trim() && attachedFiles.length === 0) return;
    if (!sessionId) return;

    const currentInput = postDescription.trim();
    const filesToSend = [...attachedFiles];
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: currentInput,
      attachedFiles: filesToSend,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setChatMessages(prev => [...prev, userMessage]);

    setPostDescription('');
    setAttachedFiles([]);
    setIsAIResponding(true);

    try {
      let processedFiles = [];
      if (filesToSend.length > 0) {
        for (const f of filesToSend) {
          const isImage = f.type && f.type.startsWith('image/');
          if (f.file) {
            try {
              const uploadedUrl = await uploadImageToSupabase(f.file);
              processedFiles.push({ name: f.name, type: f.type, url: uploadedUrl, isImage: isImage });
            } catch (e) {
              processedFiles.push({ name: f.name, type: f.type, base64: f.base64, isImage: isImage });
            }
          } else {
            processedFiles.push({ name: f.name, type: f.type, base64: f.base64, isImage: isImage });
          }
        }
      }
      setChatMessages(prev => prev.map(msg => 
        msg.id === userMessage.id ? { ...msg, attachedFiles: processedFiles } : msg
      ));

      const payload = {
        sessionId: sessionId,
        userId: localStorage.getItem('userId'),
        message: currentInput,
        postObjective: postObjective,
        toneStyle: toneStyle,
        attachedFiles: processedFiles,
        chatHistory: chatMessages.slice(-10)
};

      const response = await fetch('https://n8n.acespireconsulting.com/webhook/dev/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
        // signal: AbortSignal.timeout(30000)
      });

      if (response.ok) {
        const result = await response.json();
        const data = Array.isArray(result) ? result[0] : result;
        setChatMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'ai',
          // content: data.output || data.message || '',
          // image_url: data.permanentChatImageUrl || data.image_url || null,
           content: data.message || data.output || '',         // message first
  image_url: data.image_url || data.permanentChatImageUrl || null,  // image_url first
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }]);
      } else {
        throw new Error("Backend response not OK");
      }
    } catch (error) {
      setChatMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'ai',
        content: `I see you want to discuss: "${currentInput}". I'm having trouble connecting to my servers right now. Could you try again?`,
        image_url: null,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]);
    } finally {
      setIsAIResponding(false);
    }
  };

  const years = Array.from({length: 10}, (_, i) => new Date().getFullYear() + i);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };
  
  const days = Array.from({length: getDaysInMonth(scheduleMonth, scheduleYear)}, (_, i) => i + 1);
  const hours = Array.from({length: 12}, (_, i) => i + 1);
  const minutes = Array.from({length: 12}, (_, i) => i * 5);
  const timezones = [
    'UTC-12:00 (Baker Island)', 'UTC-11:00 (American Samoa)', 'UTC-10:00 (Hawaii)',
    'UTC-09:00 (Alaska)', 'UTC-08:00 (Pacific Time)', 'UTC-07:00 (Mountain Time)',
    'UTC-06:00 (Central Time)', 'UTC-05:00 (Eastern Time)', 'UTC-04:00 (Atlantic Time)',
    'UTC-03:00 (Argentina)', 'UTC-02:00 (South Georgia)', 'UTC-01:00 (Azores)',
    'UTC+00:00 (GMT/UTC)', 'UTC+01:00 (Central European)', 'UTC+02:00 (Eastern European)',
    'UTC+03:00 (Moscow)', 'UTC+04:00 (Gulf)', 'UTC+05:00 (Pakistan)', 'UTC+05:30 (IST)',
    'UTC+06:00 (Bangladesh)', 'UTC+07:00 (Thailand)', 'UTC+08:00 (China)',
    'UTC+09:00 (Japan)', 'UTC+10:00 (Australia East)', 'UTC+11:00 (Solomon Islands)',
    'UTC+12:00 (New Zealand)'
  ];

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    const processedFiles = await Promise.all(files.map(async (file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            file: file, 
            name: file.name,
            size: file.size,
            type: file.type,
            base64: e.target.result.split(',')[1],
            fullBase64: e.target.result
          });
        };
        reader.readAsDataURL(file);
      });
    }));
    
    setAttachedFiles(prev => [...prev, ...processedFiles]);
  };

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFormattedDate = () => {
    return `${months[scheduleMonth]} ${scheduleDay}, ${scheduleYear}`;
  };

  const generateDrafts = async () => {
    setIsGeneratingDrafts(true);
    
    try {
      let processedFiles = [];
      if (attachedFiles.length > 0) {
        for (const f of attachedFiles) {
          const isImage = f.type && f.type.startsWith('image/');
          if (f.file) {
            try {
              const uploadedUrl = await uploadImageToSupabase(f.file);
              processedFiles.push({ name: f.name, type: f.type, url: uploadedUrl, isImage: isImage });
            } catch (e) {
              console.error(`Upload failed for ${f.name}, falling back to base64`, e);
              processedFiles.push({ name: f.name, type: f.type, base64: f.base64, isImage: isImage });
            }
          } else {
            processedFiles.push({ name: f.name, type: f.type, base64: f.base64, isImage: isImage });
          }
        }
      }

      const payload = {
        prompt: postDescription || draftSeed?.content || (chatMessages.length > 0 ? chatMessages[chatMessages.length - 2]?.content : ''),
        seedContent: draftSeed?.content || null,
        seedImageUrl: draftSeed?.imageUrl || null,
        userUploadedImageUrl: null,
        postObjective: postObjective,
        toneStyle: toneStyle,
        attachedFiles: processedFiles,
        chatContext: chatMessages.slice(-6)
      };

      const response = await fetch('https://n8n.acespireconsulting.com/webhook/webhook/dev/create-post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },  
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const responseText = await response.text();
        
        let result;
        try {
          result = JSON.parse(responseText);
        } catch (parseError) {
          try {
            result = JSON.parse(JSON.parse(responseText));
          } catch (doubleParseError) {
            alert(`N8N returned invalid JSON.`);
            setIsGeneratingDrafts(false);
            return;
          }
        }
        
        let draftsArray = [];
        
        if (Array.isArray(result)) {
          draftsArray = result;
        } else if (result && result.success && result.drafts) {
          if (Array.isArray(result.drafts)) {
            draftsArray = result.drafts;
          } else if (typeof result.drafts === 'string') {
            try {
              draftsArray = JSON.parse(result.drafts);
            } catch (e) {
              console.error('Failed to parse drafts string:', e);
            }
          }
        } else if (typeof result === 'string') {
          try {
            draftsArray = JSON.parse(result);
          } catch (e) {
            console.error('Failed to parse result string:', e);
          }
        }
        
        if (Array.isArray(draftsArray) && draftsArray.length > 0) {
          const transformedDrafts = draftsArray.map((item, index) => ({
            id: item.id || index + 1,
            user: 'Acespire Solutions',
            date: 'now • 3rd',
            content: item.draft_content || item.content || 'No content available',
            translation: 'See translation',
            image: item.media_url || null,
            facts: `Generated with ${item.tone_style || 'custom'} tone for ${item.post_objective || 'general'} objective`,
            engagement: { likes: 0, comments: 0, reposts: 0, shares: 0 },
            media_url: item.media_url,
            attached_file: item.attached_file
          }));
          
          setDrafts(transformedDrafts);
          setPanelMode('drafts');
          window.history.pushState({ page: 'drafts' }, '');
        } else {
          alert(`Error: Expected array of drafts but got: ${JSON.stringify(result)}`);
          setIsGeneratingDrafts(false);
          return;
        }
      } else {
        const errorText = await response.text();
        alert(`N8N Error ${response.status}: ${response.statusText}\n${errorText}`);
        setIsGeneratingDrafts(false);
        return;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        alert('Request timed out. N8N workflow may be taking too long to process.');
      } else {
        alert(`Network error: ${error.message}\n\nPlease check:\n1. N8N workflow is active\n2. Webhook URL is accessible\n3. Your internet connection`);
      }
      setIsGeneratingDrafts(false);
      return;
    } finally {
      setIsGeneratingDrafts(false);
    }
  };

  const fetchPostData = async (postId, mode) => {
    try {
      const response = await fetch('https://n8n.acespireconsulting.com/webhook/webhook/dev/get-post-by-id', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postId: parseInt(postId) })
      });

      const rawText = await response.text();
      
      if (!rawText) {
        alert('Empty response from server');
        navigate('/scheduled-posts');
        return;
      }

      let responseData;
      try {
        responseData = JSON.parse(rawText);
      } catch (parseError) {
        alert(`Invalid response format`);
        navigate('/scheduled-posts');
        return;
      }

      if (response.ok) {
        const data = Array.isArray(responseData) ? responseData[0] : responseData;
        
        if (!data || !data.success || !data.post) {
          alert('Post not found or already deleted');
          navigate('/scheduled-posts');
          return;
        }
        
        const post = data.post;
        let promptText = post.prompt;
        if (!promptText && post.content) {
          const firstSentence = post.content.split(/[.!?]/)[0];
          promptText = firstSentence.length > 150 ? post.content.substring(0, 150) + '...' : firstSentence;
        }
        
        const draftData = [{
          id: 1,
          user: 'Acespire Solutions',
          date: 'now • 3rd',
          content: post.content,
          translation: 'See translation',
          image: post.media_url,
          facts: `Originally scheduled for ${new Date(post.scheduled_time).toLocaleDateString()}`,
          engagement: { likes: 0, comments: 0, reposts: 0, shares: 0 },
          media_url: post.media_url
        }];
        
        const scheduledDate = new Date(post.scheduled_time);
        
        await new Promise(resolve => {
          setPostDescription(promptText || '');
          setPostObjective(post.post_objective || '');
          setToneStyle(post.tone_style || '');
          setScheduleYear(scheduledDate.getFullYear());
          setScheduleMonth(scheduledDate.getMonth());
          setScheduleDay(scheduledDate.getDate());
          setScheduleHour(scheduledDate.getHours() % 12 || 12);
          setScheduleMinute(scheduledDate.getMinutes());
          setScheduleAMPM(scheduledDate.getHours() >= 12 ? 'PM' : 'AM');
          setDrafts(draftData);
          setSelectedDraft(1);
          
          setTimeout(resolve, 50);
        });
        
        if (mode === 'reschedule') {
          setPanelMode('schedule');
        } else {
          setDraftSeed({
            content: post.content || promptText || '',
            imageUrl: post.media_url || null,
            sourceMessageId: null
          });
          setLocalContent(post.content || promptText || '');
          setLocalImageUrl(post.media_url || null);
          setExtraInput('');
          setPanelMode('draft-seed');
        }
      }
    } catch (error) {
      alert(`Failed to load post data: ${error.message}`);
      navigate('/scheduled-posts');
    }
  };

  // Shared by both single-post scheduling and group-post scheduling so both
  // respect the exact same date/time picker.
  const getScheduledDateTimeISO = () => {
    let hour24 = scheduleHour;
    if (scheduleAMPM === 'PM' && scheduleHour !== 12) {
      hour24 = scheduleHour + 12;
    } else if (scheduleAMPM === 'AM' && scheduleHour === 12) {
      hour24 = 0;
    }
    const istDateString = `${scheduleYear}-${String(scheduleMonth + 1).padStart(2, '0')}-${String(scheduleDay).padStart(2, '0')}T${String(hour24).padStart(2, '0')}:${String(scheduleMinute).padStart(2, '0')}:00+05:30`;
    return new Date(istDateString).toISOString();
  };

  const schedulePost = async () => {
    const accountIdFromStorage = localStorage.getItem('selectedLinkedInAccountId');
    
    if (!accountIdFromStorage) {
      saveSchedulingState();
      setShowLinkedInModal(true);
      return;
    }
    
    const linkedinStatusFromStorage = localStorage.getItem('linkedinStatus');
    if (!linkedinStatusFromStorage || linkedinStatusFromStorage !== 'connected') {
      localStorage.setItem('linkedinStatus', 'connected');
      setLinkedinStatus('connected');
    }
    
    const selectedDraftData = drafts.find(d => d.id === selectedDraft);
    
    let hour24 = scheduleHour;
    if (scheduleAMPM === 'PM' && scheduleHour !== 12) {
      hour24 = scheduleHour + 12;
    } else if (scheduleAMPM === 'AM' && scheduleHour === 12) {
      hour24 = 0;
    }
    
    const istDateString = `${scheduleYear}-${String(scheduleMonth + 1).padStart(2, '0')}-${String(scheduleDay).padStart(2, '0')}T${String(hour24).padStart(2, '0')}:${String(scheduleMinute).padStart(2, '0')}:00+05:30`;
    const scheduledDateUTC = new Date(istDateString);
    const linkedinAccountIdInt = parseInt(accountIdFromStorage);

    const schedulePayload = {
      post_content: selectedDraftData.content,
      media_url: selectedDraftData.image,
      scheduled_datetime: scheduledDateUTC.toISOString(),
      timezone: 'Asia/Kolkata',
      user_id: localStorage.getItem('userId'),
      linkedin_email: localStorage.getItem('linkedinEmail'),
      linkedinAccountId: linkedinAccountIdInt,
      draft_id: selectedDraft,
      prompt: postDescription,
      post_objective: postObjective,
      tone_style: toneStyle
    };

    if (editMode === 'reschedule' && postIdToEdit) {
      schedulePayload.postId = postIdToEdit;
    }

    try {
      const endpoint = editMode === 'reschedule' 
        ? 'https://n8n.acespireconsulting.com/webhook/webhook/dev/update-scheduled-post'
        : 'https://n8n.acespireconsulting.com/webhook/dev/schedule-post';

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(schedulePayload),
      });

      if (response.ok) {
        clearSchedulingState();
        setPanelMode('confirmation');
      } else {
        const errorText = await response.text();
        alert('Failed to schedule post: ' + errorText);
      }
    } catch (error) {
      alert('Error scheduling post: ' + error.message);
    }
  };

  const getFormattedTime = () => {
    const displayHour = scheduleHour;
    const displayMinute = scheduleMinute.toString().padStart(2, '0');
    return `${displayHour}:${displayMinute} ${scheduleAMPM}`;
  };

  const handleLinkedInConnected = (newAccountId) => {  
    localStorage.setItem('linkedinStatus', 'connected');
    setLinkedinStatus('connected');
    
    if (newAccountId) {
      localStorage.setItem('selectedLinkedInAccountId', newAccountId.toString());
    }
    
    setShowLinkedInModal(false);
    window.history.replaceState({ page: 'create-post' }, '', '/create-post');
    window.dispatchEvent(new Event('accountChanged'));
  };

  // --- Group posting handlers ---
  const N8N_BASE = 'https://n8n.acespireconsulting.com/webhook';

  const fetchGroups = async () => {
    const accountId = localStorage.getItem('selectedLinkedInAccountId');
    if (!accountId) return;
    setIsLoadingGroups(true);
    try {
      const response = await fetch(`${N8N_BASE}/get-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ linkedinAccountId: parseInt(accountId) }),
      });
      const data = await response.json();
      if (data.success) {
        setLinkedinGroups(Array.isArray(data.groups) ? data.groups : []);
      }
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  const scrapeGroupsFromLinkedIn = async () => {
    const accountId = localStorage.getItem('selectedLinkedInAccountId');
    const userId = localStorage.getItem('userId');
    if (!accountId) {
      alert('Connect a LinkedIn account first.');
      return;
    }
    setIsScrapingGroups(true);
    try {
      const response = await fetch(`${N8N_BASE}/scrape-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ linkedinAccountId: parseInt(accountId), user_id: userId }),
      });
      const data = await response.json();
      if (data.success) {
        await fetchGroups();
      } else {
        alert('Could not refresh groups from LinkedIn: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error refreshing groups: ' + error.message);
    } finally {
      setIsScrapingGroups(false);
    }
  };

  const addGroupManually = async () => {
    const accountId = localStorage.getItem('selectedLinkedInAccountId');
    const userId = localStorage.getItem('userId');
    if (!newGroupUrl.trim()) {
      alert('Enter a LinkedIn group URL.');
      return;
    }
    setIsAddingGroup(true);
    try {
      const response = await fetch(`${N8N_BASE}/add-group-manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          linkedinAccountId: parseInt(accountId),
          user_id: userId,
          group_name: newGroupName,
          group_url: newGroupUrl,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setNewGroupName('');
        setNewGroupUrl('');
        setShowAddGroupForm(false);
        await fetchGroups();
      } else {
        alert('Could not add group: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error adding group: ' + error.message);
    } finally {
      setIsAddingGroup(false);
    }
  };

  const toggleGroupSelection = (groupId) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const postToSelectedGroups = async () => {
    if (selectedGroupIds.length === 0) {
      alert('Select at least one group to post to.');
      return;
    }
    const selectedDraftData = drafts.find((d) => d.id === selectedDraft);
    if (!selectedDraftData) {
      alert('No draft data available to post.');
      return;
    }
    const accountId = localStorage.getItem('selectedLinkedInAccountId');
    const userId = localStorage.getItem('userId');

    setIsPostingToGroups(true);
    try {
      const response = await fetch(`${N8N_BASE}/post-to-groups-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          linkedinAccountId: parseInt(accountId),
          content: selectedDraftData.content,
          media_url: selectedDraftData.image || null,
          groupIds: selectedGroupIds,
          windowMinutes: groupWindowMinutes,
          scheduledStartTime: getScheduledDateTimeISO(),
        }),
      });
      const data = await response.json();
      if (data.success) {
        setGroupBatch(data);
        setGroupBatchStatus(null);
      } else {
        alert('Could not start group posting: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error posting to groups: ' + error.message);
    } finally {
      setIsPostingToGroups(false);
    }
  };

  const pollGroupBatchStatus = async (batchId) => {
    try {
      const response = await fetch(`${N8N_BASE}/get-group-batch-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchId }),
      });
      const data = await response.json();
      if (data.success) {
        setGroupBatchStatus(data);
      }
    } catch (error) {
      console.error('Failed to poll batch status:', error);
    }
  };

  // Fetch the saved group directory the first time the "Post to Groups" tab is opened
  useEffect(() => {
    if (postDestination === 'groups' && linkedinGroups.length === 0 && !isLoadingGroups) {
      fetchGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postDestination]);

  // Poll batch progress every 15s while a batch is in progress; stops itself
  // once the batch reaches a terminal status.
  useEffect(() => {
    if (!groupBatch?.batch_id) return;
    if (groupBatchStatus?.batch?.status && groupBatchStatus.batch.status !== 'in_progress') return;

    const delay = groupBatchStatus ? 15000 : 0;
    const timer = setTimeout(() => pollGroupBatchStatus(groupBatch.batch_id), delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBatch, groupBatchStatus]);

  const Step1_Prompt = () => {
    return (
      <div className="w-full -mt-4">
        <div className="flex items-center justify-between w-full mb-0 py-1.5 px-4 bg-gray-50 border border-gray-200 rounded-md shadow-sm">
          <div className="flex items-center gap-2  relative ">
            <div className="w-4 h-4 rounded-md bg-purple-100 flex items-center justify-center text-purple-600">
              <Sparkles className="w-4 h-4" /> 
            </div>
            <div>
              <h1 className="text-m font-bold text-gray-800 leading-tight">DigiMark AI</h1>
              <p className="text-xs text-gray-500">content assistant</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button 
              onClick={() => { fetchSavedDrafts(); setShowSavedDrafts(true); }}
              className="flex items-center gap-2 px-3 py-3 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Bookmark className="w-4 h-4 text-gray-500" />
              Saved drafts
            </button>
            
            <button 
              onClick={() => { createNewSession(); setChatMessages([]); }}
              className="flex items-center gap-2 px-3 py-3 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Plus className="w-4 h-4 text-gray-500" />
              New chat
            </button>
          </div>
        </div>

        <div className="bg-white rounded-md shadow-sm border border-gray-100 overflow-hidden flex flex-col h-[calc(100vh-120px)] w-full justify-between">
          {chatMessages.length > 0 && (
            <div className="px-6 mb-4 flex-1 flex flex-col min-h-0">
              <div 
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto p-4 pb-8 space-y-8 scroll-smooth"
              >
                {chatMessages.map((message) => (
                  <div id={`message-${message.id}`} key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-3xl w-full ${message.type === 'user' ? 'flex justify-end' : ''}`}>
                      <div className={`flex items-start gap-3 w-full ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 ${
                          message.type === 'user' ? 'bg-transparent' : 'bg-purple-100 text-purple-600'
                        }`}>
                          {message.type === 'user' ? null : <Sparkles className="w-4 h-4" />}
                        </div>

                        <div className={`relative max-w-[85%] ${message.type === 'user' ? 'items-end' : 'items-start w-full'}`}>
                          
                          {message.type === 'user' ? (
                            <div className="relative group">
                              {editingMessageId === message.id ? (
                                <div className="rounded-2xl px-4 py-3 bg-white border-2 border-[#5B4AF0] w-full min-w-[280px] shadow-sm">
                                  <textarea
                                    value={editingMessageContent}
                                    onChange={(e) => setEditingMessageContent(e.target.value)}
                                    autoFocus
                                    rows={3}
                                    className="w-full text-sm text-gray-800 resize-none focus:outline-none bg-transparent"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        setEditingMessageId(null);
                                      }
                                    }}
                                  />
                                  <div className="flex justify-end gap-2 mt-2">
                                    <button
                                      onClick={() => setEditingMessageId(null)}
                                      className="px-3 py-1.5 text-xs rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => handleSaveEdit(message.id, editingMessageContent)}
                                      disabled={!editingMessageContent.trim()}
                                      className="px-3 py-1.5 text-xs rounded-md bg-[#5B4AF0] text-white hover:bg-[#4a3ad9] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                    >
                                      Save & Submit
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="rounded-2xl px-5 py-3.5 bg-[#5B4AF0] text-white">
                                  
                                  {/* 🚀 THE FIX: Gemini-Style Image/File Rendering inside the bubble */}
                                  {message.attachedFiles && message.attachedFiles.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-3">
                                      {message.attachedFiles.map((file, idx) => (
                                        file.isImage ? (
                                          <img 
                                            key={idx} 
                                            src={file.url || `data:${file.type};base64,${file.base64}`} 
                                            alt="User upload" 
                                            className="w-40 h-40 object-cover rounded-xl border border-white/20 shadow-sm"
                                          />
                                        ) : (
                                          <div key={idx} className="flex items-center gap-2 bg-white/20 px-3 py-2 rounded-lg text-sm border border-white/10">
                                            <Paperclip className="w-4 h-4 text-white" />
                                            <span className="truncate max-w-[150px] text-white">{file.name}</span>
                                          </div>
                                        )
                                      ))}
                                    </div>
                                  )}

                                  {message.edited && (
                                    <span className="text-[10px] mr-1 opacity-60 text-gray-200">(edited)</span>
                                  )}
                                  {message.content && (
                                    <div className="text-[15px] leading-relaxed mb-2 whitespace-pre-wrap">
                                      {message.content}
                                    </div>
                                  )}
                                  <p className="text-xs mt-1 opacity-70 text-gray-200">{message.timestamp}</p>
                                </div>
                              )}
                              {editingMessageId !== message.id && (
                                <div className="absolute -bottom-8 right-0 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                                  <button onClick={() => handleCopyMessage(message.id, message.content)} title="Copy text" className="w-7 h-7 flex items-center justify-center rounded-md bg-white border border-gray-200 shadow-sm text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-all">
                                    {copiedMessageId === message.id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                                  </button>
                                  <button onClick={() => { setEditingMessageId(message.id); setEditingMessageContent(message.content || ''); }} title="Edit prompt" className="w-7 h-7 flex items-center justify-center rounded-md bg-white border border-gray-200 shadow-sm text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-all">
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => handleRegenerateMessage(message.id)} title="Regenerate from this point" className="w-7 h-7 flex items-center justify-center rounded-md bg-white border border-gray-200 shadow-sm text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-all">
                                    <Repeat2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className={`group relative rounded-2xl px-4 py-3 bg-white text-gray-800 shadow-sm border border-gray-100 w-full`}>
                              {message.edited && (
                                <span className="text-[10px] mr-1 opacity-60 text-gray-400">(edited)</span>
                              )}
                              {message.content && (
                                <p className="text-sm leading-relaxed whitespace-pre-wrap mb-2">{message.content}</p>
                              )}
                              {message.image_url && (
                                <div className="relative rounded-xl overflow-hidden mb-2" style={{ maxWidth: '400px' }}>
                                  <img 
                                    src={imageBlobUrls[message.image_url] || message.image_url}
                                    alt="AI generated"
                                    className="w-full object-cover rounded-xl"
                                    onLoad={() => loadImageWithAuth(message.image_url)}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                  <button
                                    onClick={() => handleDownloadImage(message.image_url, message.id)}
                                    className="absolute bottom-2 right-2 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50"
                                    title="Download image"
                                  >
                                    <Download className="w-3 h-3 text-gray-600" />
                                  </button>
                                </div>
                              )}
                              <p className="text-xs mt-1 opacity-70 text-gray-500">{message.timestamp}</p>
                              <div className="absolute -bottom-8 left-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                                {(message.content || message.image_url) && (
                                  <button
                                    onClick={() => handlePostToLinkedIn(message)}
                                    className="flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors"
                                    title="Post to LinkedIn"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    <span>Post to LinkedIn</span>
                                  </button>
                                )}
                                <button
                                  onClick={() => handleRegenerateMessage(message.id)}
                                  className="flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                                  title="Regenerate"
                                >
                                  <Repeat2 className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={(e) => {
                                  e.preventDefault();   
                                  e.stopPropagation();  
                                  handleCopyMessage(message.id, message.content);
                                  }}
                                  className="flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                                  title="Copy"
                                >
                                  {copiedMessageId === message.id 
                                    ? <Check className="w-3 h-3 text-green-500" /> 
                                    : <Copy className="w-3 h-3" />
                                  }
                                </button>
                                {message.image_url && (
                                  <button
                                    onClick={() => handleDownloadImage(message.image_url, message.id)}
                                    className="flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                                    title="Download image"
                                  >
                                    <Download className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}     
                
                {isAIResponding && (
                  <div className="flex justify-start">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-white flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border">
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="px-6 pb-6 pt-4 mt-auto max-w-3xl mx-auto w-full">
            
            {/* 🚀 THE NEW FIX: Unified input bar container wrapper */}
            <div className="flex flex-col bg-white border border-gray-300 rounded-2xl shadow-sm focus-within:border-[#4A3AFF] focus-within:ring-1 focus-within:ring-[#4A3AFF] transition-all overflow-hidden p-2">
              
              {/* Modern Horizontal Media Strip (ChatGPT/Gemini Style) */}
              {attachedFiles.length > 0 && (
                <div className="flex flex-wrap gap-3 p-2 mb-2 max-h-24 overflow-y-auto border-b border-gray-100">
                  {attachedFiles.map((file, index) => {
                    const isImage = file.type && file.type.startsWith('image/');
                    return (
                      <div key={index} className="relative group w-16 h-16 bg-gray-50 rounded-xl border border-gray-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                        {isImage ? (
                          <img 
                            src={file.fullBase64 || `data:${file.type};base64,${file.base64}`} 
                            alt="Preview asset" 
                            className="w-full h-full object-cover" 
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center text-center px-1">
                            <Paperclip className="w-4 h-4 text-purple-600 mb-0.5" />
                            <span className="text-[9px] text-gray-500 font-medium max-w-[55px] truncate">{file.name}</span>
                          </div>
                        )}
                        {/* Overlay Dismiss Vector */}
                        <button 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeFile(index);
                          }}
                          className="absolute top-1 right-1 w-5 h-5 bg-gray-900/70 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors shadow-sm opacity-0 group-hover:opacity-100"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Input Action Controls Bar */}
              <div className="flex items-center px-2 py-1">
                <div className="flex-shrink-0">
                  <input 
                    type="file" 
                    id="inline-file-input" 
                    multiple 
                    accept="image/*,video/*,.pdf,.doc,.docx" 
                    onChange={handleFileUpload} 
                    onClick={(e) => (e.target.value = null)} 
                    className="hidden" 
                    disabled={isGeneratingDrafts || isAIResponding} 
                  />
                  <label htmlFor="inline-file-input" className="cursor-pointer text-gray-400 hover:text-gray-600 flex items-center justify-center p-1.5 hover:bg-gray-50 rounded-full transition-colors">
                    <Paperclip className="w-5 h-5" />
                  </label>
                </div>

                <textarea
                  ref={textareaRef}
                  value={postDescription}
                  onChange={handleTextInput}
                  onKeyPress={handleKeyPress}
                  placeholder="Message DigiMark AI..."
                  className="flex-1 bg-transparent border-none focus:ring-0 resize-none text-sm px-3 py-2 outline-none max-h-32 min-h-[36px]"
                  rows={1}
                  disabled={isGeneratingDrafts || isAIResponding}
                />

                <div className="flex-shrink-0 ml-2">
                  <button
                    onClick={sendMessageToAI}
                    disabled={(!postDescription.trim() && attachedFiles.length === 0) || isAIResponding || isGeneratingDrafts}
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      (postDescription.trim() || attachedFiles.length > 0) && !isAIResponding && !isGeneratingDrafts
                        ? 'bg-[#4A3AFF] text-white hover:bg-blue-700 shadow-md'
                        : 'bg-[#E5E5EA] text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {isAIResponding ? (
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <ArrowUp className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  };

  const Step_DraftSeed = () => {
    
    const handleRemoveSeedImage = () => {
      setLocalImageUrl(null);
    };

    const handleReplaceImage = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      setIsUploadingLocal(true);
      try {
        const url = await uploadImageToSupabase(file);
        setLocalImageUrl(url);
      } catch (err) {
        alert('Image upload failed. Please try again.');
      } finally {
        setIsUploadingLocal(false);
      }
    };

    const handleGenerate = async () => {
      setDraftSeed(prev => ({
        ...prev,
        content: localContent,
        imageUrl: localImageUrl
      }));
      
      setIsGeneratingDrafts(true);
      try {
        let processedFiles = [];
        if (attachedFiles.length > 0) {
          for (const f of attachedFiles) {
            const isImage = f.type && f.type.startsWith('image/');
            if (f.file) {
              try {
                const uploadedUrl = await uploadImageToSupabase(f.file);
                processedFiles.push({ name: f.name, type: f.type, url: uploadedUrl, isImage: isImage });
              } catch (e) {
                console.error(`Upload failed for ${f.name}, falling back to base64`, e);
                processedFiles.push({ name: f.name, type: f.type, base64: f.base64, isImage: isImage });
              }
            } else {
              processedFiles.push({ name: f.name, type: f.type, base64: f.base64, isImage: isImage });
            }
          }
        }

        const payload = {
          prompt: extraInput.trim() !== '' ? extraInput.trim() : (localContent ? "Create 3 distinct, highly engaging LinkedIn post variations based on the provided seed content." : ''),
          seedContent: localContent || null,
          seedImageUrl: localImageUrl || null,
          userUploadedImageUrl: null,
          postObjective: postObjective,
          toneStyle: toneStyle,
          attachedFiles: processedFiles,
          chatContext: chatMessages.slice(-6)
};

        const response = await fetch('https://n8n.acespireconsulting.com/webhook/webhook/dev/create-post', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const responseText = await response.text();
          let result;
          try { result = JSON.parse(responseText); } catch { result = JSON.parse(JSON.parse(responseText)); }
          
          let draftsArray = Array.isArray(result) ? result : (result?.drafts || []);
          if (typeof draftsArray === 'string') draftsArray = JSON.parse(draftsArray);
          
          if (Array.isArray(draftsArray) && draftsArray.length > 0) {
            const transformedDrafts = draftsArray.map((item, index) => {
              const finalImage = localImageUrl || item.media_url || null;
              
              return {
                id: item.id || index + 1,
                user: 'Acespire Solutions',
                date: 'now • 3rd',
                content: item.draft_content || item.content || 'No content available',
                translation: 'See translation',
                image: finalImage,
                facts: `Generated with ${item.tone_style || 'custom'} tone`,
                engagement: { likes: 0, comments: 0, reposts: 0, shares: 0 },
                media_url: finalImage,
                attached_file: item.attached_file
              };
            });
            
            setDrafts(transformedDrafts);
            setPanelMode('drafts');
            window.history.pushState({ page: 'drafts' }, '');
          } else {
            alert('Failed to generate drafts. Please try again.');
          }
        } else {
          alert(`N8N Error ${response.status}: ${response.statusText}`);
        }
      } catch (err) {
        alert('Network error: ' + err.message);
      } finally {
        setIsGeneratingDrafts(false);
      }
    };

    const canGenerate = localContent || localImageUrl;
    return (
      <div className="max-w-2xl mx-auto mt-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setPanelMode('chat')} 
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors" 
              title="Back to chat"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Review Seed Content</h2>
              <p className="text-sm text-gray-500">Refine the base content below, then generate 3 LinkedIn post variations</p>
            </div>
          </div>
          <div className="flex items-center gap-3 self-start sm:self-auto shrink-0">
            {/* <button 
              onClick={() => {
                if (!localContent && !localImageUrl) {
                  alert("Please add some text or an image to save a draft.");
                  return;
                }
                handleSaveDraft({ content: localContent || 'Seed draft', image: localImageUrl });
              }} 
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-100 rounded-md hover:bg-blue-100 transition-colors shrink-0"
            >
              <Bookmark className="w-4 h-4" /> Save Draft
            </button>
            <button onClick={() => setShowLinkedInModal(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#0A66C2] rounded-md hover:bg-[#004182] transition-colors shrink-0">
              Connect LinkedIn
            </button> */}
          </div>
        </div>

        <div className="bg-white rounded-2xl border-2 border-[#4A3AFF] p-5 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-[#4A3AFF] flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              Seed content from your chat
            </span>
            <span className="text-xs text-gray-400">Edit freely — this is your starting point</span>
          </div>

          <textarea
            value={localContent}
            onChange={(e) => setLocalContent(e.target.value)}
            rows={5}
            className="w-full text-[15px] leading-relaxed text-gray-800 border border-gray-200 rounded-xl p-4 focus:outline-none focus:ring-2 focus:ring-[#4A3AFF] mb-4 resize-none"
            placeholder="No text content — AI will generate text based on your image"
          />

          {localImageUrl ? (
            <div className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
              <div className="relative flex-shrink-0">
                <img 
                  src={imageBlobUrls[localImageUrl] || localImageUrl} 
                  alt="Seed layout" 
                  className="w-40 h-28 object-cover rounded-lg border border-gray-200 shadow-sm" 
                  onLoad={() => loadImageWithAuth(localImageUrl)}
                />
                <button 
                  onClick={handleRemoveSeedImage} 
                  className="absolute -top-2 -right-2 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-red-50 shadow-sm transition-colors" 
                  title="Remove image"
                >
                  <X className="w-3 h-3 text-red-500" />
                </button>
              </div>
              <div className="flex flex-col gap-2 pt-1">
                <p className="text-sm font-medium text-gray-700">Seed Image Attached</p>
                <p className="text-xs text-gray-500 mb-1">This image will be applied to your 3 draft variations.</p>
                <label className="cursor-pointer w-max">
                  <input type="file" accept="image/*" onChange={handleReplaceImage} className="hidden" disabled={isUploadingLocal} />
                  <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-700 rounded-lg border border-gray-200 transition-colors shadow-sm font-medium">
                    <Paperclip className="w-3.5 h-3.5" />
                    {isUploadingLocal ? 'Uploading...' : 'Replace with own image'}
                  </span>
                </label>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100 border-dashed">
              <div className="text-gray-400 flex-shrink-0">
                <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center shadow-sm">
                  <Sparkles className="w-5 h-5 text-[#4A3AFF]" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700">No base image provided</p>
                <p className="text-xs text-gray-500">AI will automatically generate unique images for your 3 drafts.</p>
              </div>
              <label className="cursor-pointer flex-shrink-0">
                <input type="file" accept="image/*" onChange={handleReplaceImage} className="hidden" disabled={isUploadingLocal} />
                <span className="inline-flex items-center gap-1.5 text-xs px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-lg border border-gray-200 transition-colors shadow-sm font-medium">
                  <Paperclip className="w-3.5 h-3.5" />
                  {isUploadingLocal ? 'Uploading...' : 'Upload an image'}
                </span>
              </label>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-8 shadow-sm">
          <p className="text-xs font-medium text-gray-600 mb-2">Optional Instructions</p>
          <div className="flex items-end gap-2">
            <textarea
              value={extraInput}
              onChange={(e) => {
                setExtraInput(e.target.value);
              }}
              rows={2}
              placeholder="Add specific hashtags, a call-to-action, or exact tone requirements..."
              className="flex-1 text-sm border-none outline-none resize-none text-gray-800 bg-transparent placeholder-gray-400"
            />
            {/* Phase 2 Addition: Document File Upload Input */}
            <label className="cursor-pointer p-1.5 text-gray-400 hover:text-[#4A3AFF] transition-colors">
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Paperclip className="w-5 h-5" />
            </label>
          </div>
          
          {/* Phase 2 Addition: Display uploaded files */}
          {attachedFiles.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {attachedFiles.map((file, index) => (
                <div key={index} className="flex items-center bg-blue-50 text-blue-700 px-2 py-1 rounded-md text-xs">
                  <Paperclip className="w-3 h-3 mr-1" />
                  <span className="max-w-[150px] truncate">{file.name}</span>
                  <button onClick={() => removeFile(index)} className="ml-1.5 text-blue-500 hover:text-blue-800">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="text-center pb-10">
          <button
            onClick={handleGenerate}
            disabled={isGeneratingDrafts || !canGenerate}
            className={`inline-flex items-center justify-center px-8 py-3.5 rounded-xl font-medium transition-all w-full max-w-sm ${
              isGeneratingDrafts || !canGenerate
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-[#4A3AFF] text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
            }`}
          >
            {isGeneratingDrafts ? (
              <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>Generating 3 Variations...</>
            ) : (
              <><Sparkles className="w-5 h-5 mr-2" />Generate 3 Drafts</>
            )}
          </button>
          
          {/* Phase 2 Addition: UX Helper Text */}
          <p className="text-[11px] text-gray-400 mt-3">
            {localImageUrl ? 'Will use your image for all 3 drafts' : 'AI will generate a unique image for each draft'}
            {' • '}
            {localContent ? 'Based on your seed content' : 'Content generated based on prompt'}
          </p>
        </div>
      </div>
    );
  };

  const Step2_Drafts = () => {
    if (drafts.length === 0) {
      return (
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading generated drafts...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-6xl mx-auto relative">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="text-left">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Choose Your Draft</h2>
            <p className="text-gray-600">Select the draft you'd like to schedule</p>
          </div>
          <div className="flex items-center gap-3 self-start sm:self-auto shrink-0">
            <button 
              onClick={() => {
                if (!selectedDraft) {
                  alert('Please click on one of the 3 drafts below to highlight it before saving.');
                  return;
                }
                handleSaveDraft(drafts.find(d => d.id === selectedDraft));
              }} 
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-100 rounded-md hover:bg-blue-100 transition-colors shrink-0"
            >
              <Bookmark className="w-4 h-4" /> Save Draft
            </button>
            {/* <button onClick={() => setShowLinkedInModal(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#0A66C2] rounded-md hover:bg-[#004182] transition-colors shrink-0">
              Connect LinkedIn
            </button> */}
          </div>
        </div>

        <div className="mb-6 flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700">Not happy with the AI-generated images?</p>
            <p className="text-xs text-gray-400 mt-0.5">Upload your own image - it replaces the image on ALL 3 draft cards</p>
          </div>
          {isUploadingImage && (
            <div className="flex items-center gap-2 text-xs text-blue-600">
              <div className="animate-spin w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              Uploading...
            </div>
          )}
          <label className="cursor-pointer flex-shrink-0">
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                setIsUploadingImage(true);
                try {
                  const url = await uploadImageToSupabase(file);
                  setDrafts(prev => prev.map(d => ({ ...d, image: url, media_url: url })));
                } catch {
                  alert('Image upload failed. Please try again.');
                } finally {
                  setIsUploadingImage(false);
                }
              }}
              className="hidden"
              disabled={isUploadingImage}
            />
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
              isUploadingImage
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300 cursor-pointer'
            }`}>
              <Paperclip className="w-4 h-4" />
              Upload my own image
            </span>
          </label>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {drafts.map((draft) => (
            <div
              key={draft.id}
              className={`bg-white rounded-lg p-4 shadow-sm border-2 transition-all cursor-pointer ${
                selectedDraft === draft.id ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent hover:border-gray-200'
              }`}
              onClick={() => setSelectedDraft(draft.id)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white text-sm font-semibold">AS</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">{draft.user}</h3>
                    <p className="text-xs text-gray-500">{draft.date}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleSaveDraft(draft); }}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors"
                    title="Save draft"
                  >
                    <Bookmark className="w-4 h-4" />
                  </button>

                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewDraft(draft);
                    }}
                    className="text-blue-600 text-sm hover:underline flex items-center"
                    title="Preview post"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Preview
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyDraft(draft.id, draft.content);
                    }}
                    title="Copy post content"
                    className={`flex items-center text-sm transition-colors ${
                      copiedDraftId === draft.id
                        ? 'text-green-600'
                        : 'text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    {copiedDraftId === draft.id ? (
                      <><Check className="w-4 h-4 mr-1" /><span className="text-xs font-medium">Copied!</span></>
                    ) : (
                      <><Copy className="w-4 h-4 mr-1" /></>
                    )}
                  </button>

                  {draft.image && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadImage(draft.image, draft.id);
                      }}
                      className="text-green-600 hover:text-green-700"
                      title="Download image"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{draft.content}</p>
              </div>

              {draft.image && (
                <div className="mb-3">
                  <div className="w-full bg-gray-100 rounded-lg overflow-hidden">
                    <img 
                      src={imageBlobUrls[draft.image] || draft.image} 
                      alt="Post media" 
                       className="w-full h-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    <div 
                      className="w-full h-full bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg flex items-center justify-center"
                      style={{ display: 'none' }}
                    >
                      <div className="text-center">
                        <div className="w-16 h-16 bg-blue-500 rounded-lg mx-auto mb-2 flex items-center justify-center">
                          <span className="text-white text-xl">🖼️</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">{draft.facts}</p>
                </div>
              )}

              <div className="flex items-center justify-between text-gray-500 text-xs border-t pt-3">
                <div className="flex items-center space-x-4">
                  <button className="flex items-center hover:text-blue-600">
                    <ThumbsUp className="w-4 h-4 mr-1" />
                    {draft.engagement?.likes || 0}
                  </button>
                  <button className="flex items-center hover:text-green-600">
                    <Repeat2 className="w-4 h-4 mr-1" />
                    {draft.engagement?.reposts || 0}
                  </button>
                  <button className="flex items-center hover:text-purple-600">
                    <MessageCircle className="w-4 h-4 mr-1" />
                    {draft.engagement?.comments || 0}
                  </button>
                </div>
                {selectedDraft === draft.id && (
                  <div className="text-blue-600 text-xs font-medium">Selected</div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-center space-x-4">
          <button
            onClick={() => setPanelMode('draft-seed')}
            className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Back to Edit
          </button>
          <button
            onClick={() => setPanelMode('schedule')}
            disabled={!selectedDraft}
            className={`px-8 py-3 rounded-lg font-medium transition-colors ${
              selectedDraft
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Schedule Selected Draft
          </button>
        </div>

        {previewDraft && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto no-scrollbar">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">Post Preview</h3>
                <button
                  onClick={() => setPreviewDraft(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6">
                <div className="bg-white rounded-lg border shadow-sm p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-3">
                        <span className="text-white font-semibold">AS</span>
                      </div>
                      <div>
                        <h3 className="font-semibold">{previewDraft.user}</h3>
                        <p className="text-sm text-gray-500">{previewDraft.date}</p>
                      </div>
                    </div>
                    <button className="text-gray-400">
                      <MoreHorizontal className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="mb-4">
                    <p className="text-gray-800 text-sm leading-relaxed whitespace-pre-line">{previewDraft.content}</p>
                    {previewDraft.translation && (
                      <button className="text-blue-600 text-sm hover:underline mt-2">{previewDraft.translation}</button>
                    )}
                  </div>

                  {previewDraft.image && (
                    <div className="mb-4">
                      <div className="w-full rounded-lg overflow-hidden bg-gray-100">
                        <img 
                          src={imageBlobUrls[previewDraft.image] || previewDraft.image}
                          alt="Post media" 
                          className="w-full h-auto object-contain"
                        />
                      </div>
                      {previewDraft.facts && (
                        <p className="text-sm text-gray-600 mt-2">{previewDraft.facts}</p>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between text-gray-500 border-t pt-2">
                    <div className="flex items-center space-x-6">
                      <button className="flex items-center hover:text-blue-600 transition-colors">
                        <ThumbsUp className="w-3 h-3 mr-2" />
                        <span className="text-xs">{previewDraft.engagement?.likes || 0}</span>
                      </button>
                      <button className="flex items-center hover:text-green-600 transition-colors">
                        <Repeat2 className="w-3 h-3 mr-2" />
                        <span className="text-xs">{previewDraft.engagement?.reposts || 0}</span>
                      </button>
                      <button className="flex items-center hover:text-purple-600 transition-colors">
                        <MessageCircle className="w-3 h-3 mr-2" />
                        <span className="text-xs">{previewDraft.engagement?.comments || 0}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  {previewDraft.image && (
                    <button
                      onClick={() => handleDownloadImage(previewDraft.image, previewDraft.id)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Image
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setSelectedDraft(previewDraft.id);
                      setPreviewDraft(null);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Select This Draft
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {savedDraftToast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2">
            <Check className="w-4 h-4 text-green-400" />
            {savedDraftToast}
          </div>
        )}
      </div>
    );
  };

  const Step3_Schedule = () => (
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Schedule Post</h2>
        </div>
        <div className="flex items-center gap-3 self-start sm:self-auto shrink-0">
          <button 
            onClick={() => {
              if (!selectedDraft) {
                alert('No draft data available to save.');
                return;
              }
              handleSaveDraft(drafts.find(d => d.id === selectedDraft));
            }} 
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-100 rounded-md hover:bg-blue-100 transition-colors shrink-0"
          >
            <Bookmark className="w-4 h-4" /> Save Draft
          </button>
          {/* <button onClick={() => setShowLinkedInModal(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#0A66C2] rounded-md hover:bg-[#004182] transition-colors shrink-0">
            Connect LinkedIn
          </button> */}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-3">
                <span className="text-white text-sm font-semibold">AS</span>
              </div>
              <div>
                <h3 className="font-semibold text-sm">Acespire Solutions</h3>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button className="text-blue-600 text-sm hover:underline">Follow</button>
              <button className="text-gray-400">
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mb-3">
            <p className="text-sm text-gray-800 leading-relaxed">
              {selectedDraft ? drafts.find(d => d.id === selectedDraft)?.content : "🚗 New Car, First Service Done! 🚗\nLoving my new Hyundai i20 N-line—smooth ride & packed with features! Just completed the first service; quick, efficient, and hassle-free. Ready for many more miles ahead! 🏁\n...see more\n\n#NewCar #FirstService #SmoothDrive #Hassle-free"}
            </p>
            <button className="text-blue-600 text-sm hover:underline mt-1">See translation</button>
          </div>

          {selectedDraft && drafts.find(d => d.id === selectedDraft)?.image && (
            <div className="mb-3">
              <div className="w-full bg-gray-100 rounded-lg overflow-hidden">
                 {(() => {
        const imageUrl = drafts.find(d => d.id === selectedDraft)?.image;
        return (
          <img 
            src={imageBlobUrls[imageUrl] || imageUrl}
            alt="Post media" 
            className="w-full h-auto object-contain"
            onLoad={() => loadImageWithAuth(imageUrl)} 
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
        );
      })()}
                <div 
                  className="w-full h-full bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg flex items-center justify-center"
                  style={{ display: 'none' }}
                >
                  <div className="text-center">
                    <div className="w-20 h-20 bg-blue-500 rounded-lg mx-auto mb-2 flex items-center justify-center">
                      <span className="text-white text-2xl">🖼️</span>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-2">{drafts.find(d => d.id === selectedDraft)?.facts}</p>
            </div>
          )}

          {!selectedDraft && (
            <div className="mb-3">
              <div className="w-full h-48 bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 bg-blue-500 rounded-lg mx-auto mb-2 flex items-center justify-center">
                    <span className="text-white text-2xl">🚗</span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-2">Sample post preview</p>
            </div>
          )}

          <div className="flex items-center justify-between text-gray-500 text-xs border-t pt-3">
            <div className="flex items-center space-x-4">
              <button className="flex items-center hover:text-blue-600"><ThumbsUp className="w-4 h-4 mr-1" />0</button>
              <button className="flex items-center hover:text-green-600"><Repeat2 className="w-4 h-4 mr-1" />0</button>
              <button className="flex items-center hover:text-purple-600"><MessageCircle className="w-4 h-4 mr-1" />0</button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50 mb-4">
              <button
                onClick={() => setPostDestination('single')}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  postDestination === 'single' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'
                }`}
              >
                Post to Feed
              </button>
              <button
                onClick={() => setPostDestination('groups')}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  postDestination === 'groups' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'
                }`}
              >
                Post to Groups
              </button>
            </div>

            <h3 className="font-medium mb-3">{postDestination === 'single' ? 'Schedule' : 'Start Time'}</h3>
            <p className="text-sm text-gray-600 mb-2">
              {postDestination === 'single'
                ? <>Select a date to make your post <span className="font-medium">Public</span></>
                : 'The first group posts at this exact time; the rest post at random times within the window below.'}
            </p>
            
            <div className="mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Year</label>
                  <select
                    value={scheduleYear}
                    onChange={(e) => setScheduleYear(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Month</label>
                  <select
                    value={scheduleMonth}
                    onChange={(e) => setScheduleMonth(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {months.map((month, index) => (
                      <option key={index} value={index}>{month}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Day</label>
                  <select
                    value={scheduleDay}
                    onChange={(e) => setScheduleDay(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {days.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Hour</label>
                  <select
                    value={scheduleHour}
                    onChange={(e) => setScheduleHour(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {hours.map(hour => (
                      <option key={hour} value={hour}>{hour}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Minute</label>
                  <select
                    value={scheduleMinute}
                    onChange={(e) => setScheduleMinute(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {minutes.map(minute => (
                      <option key={minute} value={minute}>{minute.toString().padStart(2, '0')}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Period</label>
                  <select
                    value={scheduleAMPM}
                    onChange={(e) => setScheduleAMPM(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Selected</label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                    {getFormattedTime()}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-1">Time Zone</label>
                <select
                  value={scheduleTimezone}
                  onChange={(e) => setScheduleTimezone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {timezones.map(timezone => (
                    <option key={timezone} value={timezone}>{timezone}</option>
                  ))}
                </select>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-blue-800">
                  {postDestination === 'single' ? 'Scheduled for' : 'Starts at'}: {getFormattedDate()} at {getFormattedTime()} ({scheduleTimezone.split('(')[1]?.replace(')', '') || 'IST'})
                </p>
              </div>
            </div>

            {postDestination === 'groups' && (
            <>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium">Select Groups</h3>
              <button
                onClick={scrapeGroupsFromLinkedIn}
                disabled={isScrapingGroups}
                className="text-xs text-blue-600 hover:underline disabled:opacity-50"
              >
                {isScrapingGroups ? 'Refreshing...' : 'Refresh from LinkedIn'}
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Posts are spread out randomly over the window below rather than posted all at once.
            </p>

            {isLoadingGroups ? (
              <div className="text-sm text-gray-500 py-4 text-center">Loading groups...</div>
            ) : linkedinGroups.length === 0 ? (
              <div className="text-sm text-gray-500 py-4 text-center border border-dashed border-gray-300 rounded-lg">
                No groups yet. Click "Refresh from LinkedIn" or add one manually below.
              </div>
            ) : (
              <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {linkedinGroups.map(group => (
                  <label key={group.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedGroupIds.includes(group.id)}
                      onChange={() => toggleGroupSelection(group.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 truncate">{group.group_name}</p>
                      <p className="text-xs text-gray-400">{group.source === 'scraped' ? 'From LinkedIn' : 'Added manually'}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {!showAddGroupForm ? (
              <button
                onClick={() => setShowAddGroupForm(true)}
                className="mt-3 text-sm text-blue-600 hover:underline"
              >
                + Add a group manually
              </button>
            ) : (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
                <input
                  type="text"
                  placeholder="Group name"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="https://www.linkedin.com/groups/12345678/"
                  value={newGroupUrl}
                  onChange={(e) => setNewGroupUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex gap-2">
                  <button
                    onClick={addGroupManually}
                    disabled={isAddingGroup}
                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isAddingGroup ? 'Adding...' : 'Add Group'}
                  </button>
                  <button
                    onClick={() => { setShowAddGroupForm(false); setNewGroupName(''); setNewGroupUrl(''); }}
                    className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="mt-4">
              <label className="block text-xs text-gray-500 mb-1">Spread posts over</label>
              <select
                value={groupWindowMinutes}
                onChange={(e) => setGroupWindowMinutes(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
                <option value={180}>3 hours</option>
                <option value={240}>4 hours</option>
              </select>
            </div>

            {groupBatch && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">
                  Posting to {groupBatch.total_groups} group{groupBatch.total_groups !== 1 ? 's' : ''} over the next {(groupBatch.window_minutes / 60).toFixed(1)} hours
                </p>
                {groupBatchStatus?.batch && (
                  <>
                    <div className="w-full bg-blue-100 rounded-full h-2 mb-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{
                          width: `${((groupBatchStatus.batch.completed_count + groupBatchStatus.batch.failed_count) / groupBatchStatus.batch.total_groups) * 100}%`
                        }}
                      />
                    </div>
                    <p className="text-xs text-blue-700">
                      {groupBatchStatus.batch.completed_count} posted
                      {groupBatchStatus.batch.failed_count > 0 && `, ${groupBatchStatus.batch.failed_count} failed`}
                      {' '}of {groupBatchStatus.batch.total_groups}
                      {groupBatchStatus.batch.status === 'in_progress' && ' — in progress...'}
                      {groupBatchStatus.batch.status === 'completed' && ' — all done!'}
                      {groupBatchStatus.batch.status === 'completed_with_errors' && ' — done, with some failures'}
                    </p>
                    {groupBatchStatus.jobs?.some(j => j.status === 'failed') && (
                      <ul className="mt-2 text-xs text-red-600 space-y-1">
                        {groupBatchStatus.jobs.filter(j => j.status === 'failed').map(j => (
                          <li key={j.id}>• {j.group_name}: {j.error_message || 'Failed'}</li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            )}
            </>
            )}
          </div>

          <div>
            <h3 className="font-medium mb-3">Before you publish, kindly check the following:</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Make sure the content is Correct</li>
              <li>• No copyrights are found.</li>
              <li>• You can edit the post if needed.</li>
            </ul>
            
            <button
              onClick={() => setPanelMode('draft-seed')} 
              className="flex items-center px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg mt-3"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Edit Post
            </button>
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              onClick={() => setPanelMode('drafts')}
              className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Back
            </button>
            {postDestination === 'single' ? (
              <button
                onClick={schedulePost}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Schedule Post
              </button>
            ) : (
              <button
                onClick={postToSelectedGroups}
                disabled={isPostingToGroups || selectedGroupIds.length === 0 || !!groupBatch}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPostingToGroups
                  ? 'Starting...'
                  : groupBatch
                  ? 'Posting Started'
                  : `Post to ${selectedGroupIds.length} Group${selectedGroupIds.length !== 1 ? 's' : ''}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const Step4_Confirmation = () => {
    const handleDeleteScheduledPost = async () => {
      if (!window.confirm('Are you sure you want to delete this scheduled post?')) {
        return;
      }

      try {
        const response = await fetch('https://n8n.acespireconsulting.com/webhook/dev/delete-scheduled-post', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            postId: postIdToEdit || selectedDraft 
          })
        });

        const result = await response.json();

        if (response.ok && result.success) {
          alert('Post deleted successfully!');
          navigate('/scheduled-posts');
        } else {
          alert(result.error || 'Failed to delete post');
        }
      } catch (error) {
        console.error('Error deleting post:', error);
        alert('Failed to delete post. Please try again.');
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-6 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-0">
              <Calendar className="w-6 h-6 text-green-600" />
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-4 text-left mt-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-2">
                    <span className="text-white text-xs font-semibold">AS</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">Acespire Solutions</h3>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <button className="text-blue-600 text-xs hover:underline">Follow</button>
                  <button className="text-gray-400">
                    <MoreHorizontal className="w-3 h-3" />
                  </button>
                </div>
              </div>

              <div className="mb-3">
                <p className="text-sm text-gray-800 leading-relaxed">
                  {selectedDraft ? drafts.find(d => d.id === selectedDraft)?.content?.substring(0, 100) + "..." : "🚗 New Car, First Service Done! 🚗\nLoving my new Hyundai i20 N-line..."}
                </p>
              </div>
              
              {selectedDraft && drafts.find(d => d.id === selectedDraft)?.image && (
                <div className="mb-3">
                  <div className="w-full bg-gray-100 rounded-lg overflow-hidden relative">
                    {(() => {
                      const imageUrl = drafts.find(d => d.id === selectedDraft)?.image;
                      return (
                        <img 
                          src={imageBlobUrls[imageUrl] || imageUrl}  
                          alt="Post media" 
                          className="w-full h-auto object-contain"
                          onLoad={() => loadImageWithAuth(imageUrl)}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      );
                    })()}
                    <div 
                      className="w-full h-full bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg flex items-center justify-center"
                      style={{ display: 'none' }}
                    >
                      <div className="text-center">
                        <div className="w-20 h-20 bg-blue-500 rounded-lg mx-auto mb-2 flex items-center justify-center">
                          <span className="text-white text-2xl">🖼️</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">{drafts.find(d => d.id === selectedDraft)?.facts}</p>
                </div>
              )}

              <div className="flex items-center justify-between text-gray-500 text-xs border-t pt-2 mt-2">
                <div className="flex items-center space-x-3">
                  <button className="flex items-center hover:text-blue-600"><ThumbsUp className="w-3 h-3 mr-1" />0</button>
                  <button className="flex items-center hover:text-green-600"><Repeat2 className="w-3 h-3 mr-1" />0</button>
                  <button className="flex items-center hover:text-purple-600"><MessageCircle className="w-3 h-3 mr-1" />0</button>
                </div>
              </div>
            </div>

            <p className="text-gray-800 text-sm font-medium mb-1">Your post is scheduled for {getFormattedDate()} at {getFormattedTime()}</p>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/scheduled-posts')}
              className="flex-1 flex items-center justify-center px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Calendar
            </button>
            <button
              onClick={handleDeleteScheduledPost} 
              className="flex-1 flex items-center justify-center px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </button>
          </div>

          <button
            onClick={() => setPanelMode('schedule')} 
            className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
          >
            Back to Schedule
          </button>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    if (isLoadingPost) {
      return (
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading post data...</p>
          </div>
        </div>
      );
    }
    
    switch (panelMode) {
  case 'chat':
    return Step1_Prompt();
  case 'draft-seed':
    return Step_DraftSeed(); 
  case 'drafts':
    return Step2_Drafts();
  case 'schedule':
    return Step3_Schedule();
  case 'confirmation':
    return Step4_Confirmation();
  default:
    return Step1_Prompt();
}
  };
  useEffect(() => {
    if (panelMode === 'chat' && draftSeed?.sourceMessageId) {
      setTimeout(() => {
        const targetMessage = document.getElementById(`message-${draftSeed.sourceMessageId}`);
        if (targetMessage) {
          targetMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [panelMode, draftSeed]);
  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col min-w-0 lg:ml-64">
        <div className="flex-1 px-2 pb-0 pt-2 lg:px-4 lg:pb-0 lg:pt-2 overflow-hidden">
          <Header 
            showSavedDraftsButton={['draft-seed', 'drafts', 'schedule'].includes(panelMode)}
            onOpenSavedDrafts={() => { fetchSavedDrafts(); setShowSavedDrafts(true); }}
          />
          {renderCurrentStep()}
        </div>
      </div>

      {showLinkedInModal && (
        <LinkedInConnectionModal
          isOpen={showLinkedInModal}
          onClose={() => setShowLinkedInModal(false)}
          onSuccess={handleLinkedInConnected}
        />
      )}
      
      {showSavedDrafts && (
        <SavedDraftsPanel
          drafts={savedDrafts}
          onClose={() => setShowSavedDrafts(false)}
          onSelect={handleSelectSavedDraft}
          onDelete={handleDeleteSavedDraft}
        />
      )}
    </div>
  );
}; 

const SavedDraftsPanel = ({ drafts, onClose, onSelect, onDelete }) => (
  <div className="fixed inset-0 z-50 flex">
    <div 
      className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    />
    <div className="absolute right-0 top-0 bottom-0 w-96 bg-white shadow-2xl flex flex-col">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div>
          <h3 className="font-semibold text-gray-800">Saved drafts</h3>
          <p className="text-xs text-gray-400 mt-0.5">{drafts.length} draft{drafts.length !== 1 ? 's' : ''}</p>
        </div>
        <button 
          onClick={onClose}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {drafts.length === 0 ? (
          <div className="text-center py-12">
            <Bookmark className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No saved drafts yet</p>
            <p className="text-xs text-gray-300 mt-1">
              Click "Save draft" on any draft card to save it here
            </p>
          </div>
        ) : (
          drafts.map(draft => (
            <div 
              key={draft.id}
              className="bg-gray-50 rounded-xl border border-gray-200 p-3 hover:border-indigo-300 transition-colors"
            >
              <p className="text-sm text-gray-700 line-clamp-3 leading-relaxed mb-2">
                {draft.content}
              </p>
              {draft.media_url && (
                <img 
                  src={draft.media_url}
                  alt=""
                  className="w-full h-28 object-cover rounded-lg mb-2"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="flex items-center justify-between mt-2">
                <div>
                  <p className="text-xs text-gray-400">
                    {new Date(draft.created_at).toLocaleDateString('en-IN', { 
                      day: 'numeric', month: 'short', year: 'numeric' 
                    })}
                  </p>
                  {draft.tone_style && (
                    <p className="text-[10px] text-gray-400 capitalize mt-0.5">{draft.tone_style}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => onDelete(draft.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                    title="Delete draft"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <button 
                    onClick={() => onSelect(draft)}
                    className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Use this draft &rarr;
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  </div>
);

export default CreatePostPage;